import { isProduction, isStaging } from 'src/config';
// import { setSession } from 'src/redux/slices/auth';
import { waitms } from 'src/utils/wait';
import _toLower from 'lodash/toLower';
import { parseJwt } from 'src/utils/jwt';
import { getRuntimeBrandSlug } from 'src/utils/brand';
import * as Sentry from '@sentry/react';
import {
  DASHBOARD_VERSION_BASELINE_KEY,
  DASHBOARD_VERSION_LATEST_KEY,
} from 'src/utils/dashboardVersion';
import { maintenanceFetch } from './maintenance';

const getBrandHeader = () =>
  getRuntimeBrandSlug() ? { 'x-brand': getRuntimeBrandSlug() } : {};

const devOrigin =
  import.meta.env.VITE_DEV_BACKEND_ORIGIN || `${window.location.hostname}:3001`;

const setSession = (accessToken, refreshToken) => {
  if (accessToken && refreshToken) {
    localStorage.setItem('token', accessToken);
    localStorage.setItem('refresh-token', refreshToken);
  } else {
    localStorage.removeItem('token');
    localStorage.removeItem('refresh-token');
  }
};

// Clearing storage alone leaves the SPA rendering a dead session, so tell the
// app to prompt for a fresh login too.
const endSession = () => {
  setSession(null, null);
  window.dispatchEvent(
    new CustomEvent('token-expired', { detail: { isExpired: true } })
  );
};

let refreshInFlight = null;

// A fetch rejects when the page is being torn down (navigation/reload) exactly
// as it does when the network is unreachable. We must not report the first as a
// failure, and must not swallow the second — a swallowed failure surfaces to the
// user as a SUCCESS, because callers treat a resolved promise as a saved write.
// `navigator.onLine` cannot tell these apart (it only reports whether a network
// interface exists, not whether the server is reachable), so track teardown
// directly instead.
let isPageUnloading = false;
if (typeof window !== 'undefined') {
  // pagehide covers the bfcache/mobile-Safari case that beforeunload misses.
  window.addEventListener('pagehide', () => {
    isPageUnloading = true;
  });
  window.addEventListener('beforeunload', () => {
    isPageUnloading = true;
  });
}

const getDomainBasedUrl = (prodDomain, stagingDomain, devDomain) => {
  if (isProduction) {
    return window.location.hostname.includes('careitapp.com')
      ? `https://${prodDomain}.careitapp.com`
      : `https://${prodDomain}.careit.com`;
  } else if (isStaging) {
    return window.location.hostname.includes('careitapp.com')
      ? `https://${stagingDomain}.careitapp.com`
      : `https://${stagingDomain}.careit.com`;
  } else {
    return `http://${devDomain}`;
  }
};

export const BACKEND_ORIGIN = getDomainBasedUrl(
  'api',
  'staging-api',
  devOrigin
);
export const SOCKET_ORIGIN = getDomainBasedUrl(
  'api',
  'staging-api',
  devOrigin
).replace('http', 'ws');
//export const BUCKET_NAME = getDomainBasedUrl('assets', 'staging-assets', 'dev-assets');

/**
 * Constructs an http(s) address from an API route
 * (used internally by `fetchAPI`)
 * @param {string} route
 * @returns string
 */
export function getRoute(route) {
  return BACKEND_ORIGIN.concat(route);
}

export const METHODS = {
  delete: 'delete',
  get: 'get',
  post: 'post',
  put: 'put',
};

/**
 * Arbitrarily fetches the backend API.
 * @param {string} route
 * @param {string} method
 * @param {Object} data
 * @param {Object} opts
 * @param {boolean} opts.noAuthRefresh Will not attempt to use a refresh token on a 401.
 * @param {AbortSignal} opts.signal AbortSignal for cancelling the request.
 * @returns Promise<Response>
 */
export async function fetchAPI(
  route,
  method,
  data,
  { noAuthRefresh = false, headers: customHeaders, signal } = {}
) {
  try {
    const doFetch = () =>
      maintenanceFetch(getRoute(route), {
        mode: 'cors',
        method,
        headers: {
          ...(window.localStorage.getItem('token')
            ? {
                Authorization: `Bearer ${window.localStorage.getItem('token')}`,
              }
            : {}),
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'Access-Control-Allow-Origin': '*',
          ...getBrandHeader(),
          ...(customHeaders ?? {}),
        },
        body: data ? JSON.stringify(data) : undefined,
        redirect: 'follow',
        signal,
      });
    const res = await doFetch();

    try {
      const headerVersionStr = res.headers.get('x-dashboard-version');
      const headerVersion = parseInt(headerVersionStr ?? 'NaN', 10);

      const navEntries = window.performance?.getEntriesByType?.('navigation');
      const navEntry = Array.isArray(navEntries) ? navEntries[0] : undefined;
      const isReload =
        (navEntry && navEntry.type === 'reload') ||
        window.performance?.navigation?.type === 1;

      // Per-tab baseline of the version this tab first loaded. Set once on
      // first API response, and re-sync on full reload so a refreshed tab
      // matches the server and does not keep showing the snackbar.
      if (!Number.isNaN(headerVersion)) {
        const hasBaseline =
          sessionStorage.getItem(DASHBOARD_VERSION_BASELINE_KEY) != null;
        if (isReload || !hasBaseline) {
          sessionStorage.setItem(
            DASHBOARD_VERSION_BASELINE_KEY,
            String(headerVersion)
          );
        }
      }

      const prevLatest = parseInt(
        localStorage.getItem(DASHBOARD_VERSION_LATEST_KEY) ?? '0',
        10
      );
      let latest = prevLatest;
      if (!Number.isNaN(headerVersion)) {
        latest = isReload
          ? headerVersion
          : Number.isNaN(prevLatest)
            ? headerVersion
            : Math.max(prevLatest, headerVersion);
      }

      if (!Number.isNaN(latest) && latest > 0) {
        localStorage.setItem(DASHBOARD_VERSION_LATEST_KEY, String(latest));
        window.versionEvent = new CustomEvent('latest-dashboard-version', {
          detail: { latestVersion: latest },
        });
        window.dispatchEvent(window.versionEvent);
      }
    } catch (err) {
      Sentry.captureException(err);
    }

    let resData = {};
    try {
      resData = await res.clone().json();
    } catch (err) {
      resData = {};
    }

    // Account-level 403s (license lockout, declined approval) apply to every
    // request, so surface them once as a full-screen notice instead of
    // per-call error snackbars. Handled by AccountBlockedScreen in App.js.
    if (
      res.status === 403 &&
      (resData?.code === 'ACCOUNT_LICENSE_LOCKED' || resData?.declined_approval)
    ) {
      window.dispatchEvent(
        new CustomEvent('account-access-blocked', {
          detail: {
            code:
              resData?.code === 'ACCOUNT_LICENSE_LOCKED'
                ? 'ACCOUNT_LICENSE_LOCKED'
                : 'ACCOUNT_DECLINED',
            message: resData?.message,
          },
        })
      );
    }

    // If we get a 401, try to use our refresh token
    if (res.status === 401 && !noAuthRefresh) {
      // Check for the refresh token
      const refreshToken = localStorage.getItem('refresh-token');
      if (!refreshToken) {
        return res;
      }
      const { exp } = parseJwt(refreshToken);
      const expDate = new Date(exp * 1000);
      //if refresh token expired or is invalid, logout
      if (
        expDate.getTime() < new Date().getTime() ||
        _toLower(resData?.message) === 'invalid token'
      ) {
        window.modalEvent = new CustomEvent('token-expired', {
          detail: { isExpired: true },
        });
        window.dispatchEvent(window.modalEvent);
        return res;
      }

      // We should first try the global "using refresh token" lock
      const startLock = new Date();
      let retryCount = 0;
      const maxRetries = 8;
      /* eslint-disable-next-line no-constant-condition */
      while (true) {
        /* eslint-disable-next-line */
        if (localStorage.getItem('refresh-token-lock') === '1') {
          await waitms(250);
          retryCount += 1;

          if (retryCount >= maxRetries) {
            localStorage.setItem('refresh-token-lock', '0');
            endSession();
            return res;
          }

          const newRefreshToken = localStorage.getItem('refresh-token');
          if (newRefreshToken !== refreshToken) {
            // Looks like another promise managed to refresh the token!
            // Skip to the re-request.
            break;
          }

          // timeout the lock (1s)
          const now = new Date();
          if (now.valueOf() - startLock.valueOf() > 1000) {
            console.error('Timeout');
            localStorage.setItem('refresh-token-lock', '0');
            return res;
          }
        } else {
          localStorage.setItem('refresh-token-lock', '1');

          try {
            // Use the refresh token
            if (!refreshInFlight) {
              refreshInFlight = import('src/backend/auth')
                .then(({ useRefreshToken }) => useRefreshToken(refreshToken))
                // eslint-disable-next-line no-loop-func
                .finally(() => {
                  refreshInFlight = null;
                });
            }
            const newTokens = await refreshInFlight;
            setSession(newTokens.accessToken, newTokens.refreshToken, {
              noRevoke: true,
            });
          } catch (err) {
            // We cannot renew the session. Logout and return the original request result.
            console.error(err);
            endSession();

            localStorage.setItem('refresh-token-lock', '0');
            return res;
          }

          // We can retry the request now
          localStorage.setItem('refresh-token-lock', '0');
          break;
        }
      }

      // Re-run the request; await so a retry failure hits the catch below.
      return await doFetch();
    }

    return res;
  } catch (err) {
    // A request cancelled by a page reload surfaces as "NetworkError"/"Failed to
    // fetch" — identical to a genuinely unreachable server. Only swallow it when
    // the page is actually being torn down; otherwise the caller must see the
    // failure, or it will report a lost write to the user as a success.
    const isNetworkError =
      err.message.includes('NetworkError') ||
      err.message.includes('Failed to fetch');

    if (isNetworkError && isPageUnloading) {
      return {};
    }

    throw err;
  }
}

/*
 * Verify a response is not erroneous.
 */
export async function assertErrors(res, allowNon200 = false) {
  if (res.status === undefined) return;
  if (res.status !== 200) {
    let body;
    try {
      body = await res.json();
    } catch {
      const error = new Error(res.statusText);
      error.status = res.status;
      throw error;
    }

    if (body.error) {
      const error = new Error(body.message);
      error.status = res.status;
      error.code = body.code;
      error.data = body;
      throw error;
    } else {
      if (allowNon200) return;
      const error = new Error(res.statusText);
      error.status = res.status;
      throw error;
    }
  }
}

export async function parseBody(
  res,
  skipErrorCheck = false,
  fallbackValue = {}
) {
  if (!res || res.status === undefined) {
    // res is undefined/null or res.status is undefined for requests that were
    // cancelled due to reload or when the refresh token is expired.
    return fallbackValue;
  }

  let body;
  try {
    body = await res.json();
  } catch (err) {
    // Connection dropped mid-body — treat like a cancelled request.
    if (
      err instanceof TypeError &&
      (err.message.includes('Failed to fetch') ||
        err.message.includes('NetworkError') ||
        err.message.includes('network error'))
    ) {
      return fallbackValue;
    }
    // An error response without a JSON body, like Express's plain-text 403.
    if (!skipErrorCheck && !res.ok) {
      const error = new Error(
        res.statusText || `Request failed (${res.status})`
      );
      error.status = res.status;
      throw error;
    }
    throw err;
  }

  if (!skipErrorCheck) {
    if (body.error) {
      const error = new Error(
        body.message || body.errorObj?.message || 'Something went wrong'
      );
      error.status = res.status;
      if (body.code) {
        error.code = body.code;
      } else if (body.errorObj?.code) {
        error.code = body.errorObj.code;
      }
      if (body.domainIssue) {
        error.domainIssue = body.domainIssue;
      }
      if (Array.isArray(body.findings)) {
        // Newsletter lint findings that blocked a submit or send.
        error.findings = body.findings;
      }
      if (Array.isArray(body.conflictStopIds)) {
        // Deliveries another driver claimed first (distribution 409s).
        error.conflictStopIds = body.conflictStopIds;
      }
      if (body.declined_approval) {
        // The declined-org 403 carries no code field, only this flag —
        // callers distinguishing account-blocked errors need it.
        error.declinedApproval = true;
      }
      throw error;
    }
    if (res.status !== 200 && res.status !== 201)
      throw new Error(res.statusText);
  }

  return body;
}

/**
 * Arbitrarily fetches the backend API.
 * @param {string} route
 * @param {string} method
 * @param {Object} data
 * @returns Promise<Response>
 */
export async function uploadFileAPI(route, method, data) {
  return maintenanceFetch(getRoute(route), {
    mode: 'cors',
    method,
    headers: {
      ...(window.localStorage.getItem('token')
        ? { Authorization: `Bearer ${window.localStorage.getItem('token')}` }
        : {}),
      Accept: '*/*',
      'Access-Control-Allow-Origin': '*',
      ...getBrandHeader(),
    },
    body: data,
    redirect: 'follow',
  });
}
